import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { NodeWithPosition, MIN_SCALE, MAX_SCALE, SCALE_STEP } from '../../types/mapEditor';

const ManagerContainer = styled.div<{ $isCollapsed: boolean }>`
  width: ${props => props.$isCollapsed ? '48px' : '320px'};
  height: 100%;
  background: #1a1a2e;
  border-left: 1px solid #333;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  overflow: hidden;
`;

const ManagerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid #333;
  min-height: 48px;
`;

const HeaderTitle = styled.h3<{ $visible: boolean }>`
  color: #fff;
  font-size: 14px;
  margin: 0;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s;
  white-space: nowrap;
`;

const CollapseButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #2a2a3e;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const NodesList = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  gap: 8px;
`;

const NodeCard = styled.div<{ $isSelected: boolean; $isPositioned: boolean }>`
  background: ${props => props.$isSelected ? 'rgba(76, 175, 80, 0.2)' : '#0a0a15'};
  border: 1px solid ${props => {
    if (props.$isSelected) return '#4CAF50';
    if (props.$isPositioned) return '#666';
    return '#333';
  }};
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.$isSelected ? '#4CAF50' : '#555'};
  }
`;

const NodeCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const NodeIcon = styled.div<{ $type: string; $isMain: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.$isMain ? '8px' : '50%'};
  background: ${props => {
    if (props.$isMain) return 'linear-gradient(135deg, #FFD700, #FFA500)';
    switch (props.$type?.toLowerCase()) {
      case 'domination': return 'linear-gradient(135deg, #9C27B0, #7B1FA2)';
      case 'bomb': return 'linear-gradient(135deg, #FF5722, #E64A19)';
      case 'flag': return 'linear-gradient(135deg, #2196F3, #1976D2)';
      default: return 'linear-gradient(135deg, #4CAF50, #388E3C)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const NodeInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const NodeName = styled.div`
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NodeLocationWrapper = styled.div`
  position: relative;
  margin-bottom: 2px;
`;

const NodeLocation = styled.div`
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 500;
  opacity: 0.85;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    opacity: 1;
    text-decoration: underline;
    text-decoration-style: dotted;
  }
`;

const NodeLocationTooltip = styled.div`
  position: fixed;
  background: #222;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 500;
  padding: 10px 14px;
  border-radius: 6px;
  z-index: 99999;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6);
  white-space: normal;
  word-wrap: break-word;
  max-width: 300px;
  border: 1px solid #444;
`;

const NodeStatus = styled.div<{ $positioned: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #FFFFFF;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const NodeCardControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 12px 12px 12px;
  border-top: 1px solid #333;
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlLabel = styled.span`
  font-size: 12px;
  color: #FFFFFF;
  font-weight: 500;
  min-width: 50px;
`;

const PositionInput = styled.input`
  width: 70px;
  padding: 4px 8px;
  background: #0a0a15;
  border: 1px solid #333;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  font-family: monospace;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ScaleSlider = styled.input`
  flex: 1;
  height: 4px;
  appearance: none;
  background: #333;
  border-radius: 2px;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    background: #4CAF50;
    border-radius: 50%;
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ScaleValue = styled.span`
  font-size: 11px;
  color: #fff;
  min-width: 35px;
  text-align: right;
`;

const LockButton = styled.button<{ $locked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${props => props.$locked ? 'rgba(255, 152, 0, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.$locked ? '#ff9800' : '#444'};
  border-radius: 4px;
  color: ${props => props.$locked ? '#ff9800' : '#888'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$locked ? 'rgba(255, 152, 0, 0.3)' : '#2a2a3e'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  flex: 1;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  background: ${props => {
    switch (props.$variant) {
      case 'primary': return '#4CAF50';
      case 'danger': return '#f44336';
      default: return '#2a2a3e';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'primary': return '#4CAF50';
      case 'danger': return '#f44336';
      default: return '#444';
    }
  }};
  color: #FFFFFF;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #FFFFFF;
  text-align: center;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.7;
  }

  p {
    font-size: 14px;
    font-weight: 500;
    margin: 0;
  }
`;

const Instructions = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'block' : 'none'};
  padding: 12px;
  background: rgba(76, 175, 80, 0.1);
  border-top: 1px solid #333;
  font-size: 12px;
  color: #FFFFFF;
  font-weight: 500;
  text-align: center;
`;

const CollapsedIcons = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  flex-direction: column;
  gap: 4px;
  padding: 8px 4px;
  overflow-y: auto;
  align-items: center;
`;

const CollapsedNodeIcon = styled.div<{ $isPositioned: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.$isPositioned ? '#4CAF50' : '#333'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$isPositioned ? '#45a049' : '#444'};
  }
`;

interface NodePositionManagerProps {
  nodes: NodeWithPosition[];
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  onUpdatePosition: (nodeId: string, x: number, y: number, scale: number, locked: boolean) => void;
  onPlaceOnCanvas: (node: NodeWithPosition) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const NodePositionManager: React.FC<NodePositionManagerProps> = ({
  nodes,
  selectedNodeId,
  onSelectNode,
  onUpdatePosition,
  onPlaceOnCanvas,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [tooltipNodeId, setTooltipNodeId] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const handleNodeClick = useCallback((node: NodeWithPosition) => {
    if (expandedNodeId === node.id) {
      setExpandedNodeId(null);
      onSelectNode(null);
    } else {
      setExpandedNodeId(node.id);
      onSelectNode(node.id);
    }
  }, [expandedNodeId, onSelectNode]);

  const handlePositionChange = useCallback((
    node: NodeWithPosition,
    field: 'x' | 'y' | 'scale',
    value: string
  ) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    const newX = field === 'x' ? numValue : (node.positionX ?? 0);
    const newY = field === 'y' ? numValue : (node.positionY ?? 0);
    const newScale = field === 'scale' ? numValue : node.scale;

    onUpdatePosition(node.id, newX, newY, newScale, node.locked);
  }, [onUpdatePosition]);

  const handleLockToggle = useCallback((node: NodeWithPosition) => {
    onUpdatePosition(
      node.id,
      node.positionX ?? 0,
      node.positionY ?? 0,
      node.scale,
      !node.locked
    );
  }, [onUpdatePosition]);

  const handleResetPosition = useCallback((node: NodeWithPosition) => {
    onUpdatePosition(node.id, 0, 0, 1, false);
  }, [onUpdatePosition]);

  const getNodeDisplayNumber = (index: number, isMain: boolean): string => {
    return isMain ? 'M' : (index + 1).toString();
  };

  const isNodePositioned = (node: NodeWithPosition): boolean => {
    return node.positionX !== null && node.positionY !== null;
  };

  const sortedNodes = [...nodes].sort((a, b) => {
    // Main node first
    const aIsMain = a.nodeType?.toLowerCase() === 'main';
    const bIsMain = b.nodeType?.toLowerCase() === 'main';
    if (aIsMain && !bIsMain) return -1;
    if (!aIsMain && bIsMain) return 1;
    // Then by friendly name
    return (a.friendlyName || a.nodeCode).localeCompare(b.friendlyName || b.nodeCode);
  });

  return (
    <ManagerContainer $isCollapsed={isCollapsed}>
      <ManagerHeader>
        <HeaderTitle $visible={!isCollapsed}>Node Positions</HeaderTitle>
        <CollapseButton onClick={onToggleCollapse} title={isCollapsed ? 'Expand' : 'Collapse'}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            {isCollapsed ? (
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            ) : (
              <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
            )}
          </svg>
        </CollapseButton>
      </ManagerHeader>

      {/* Expanded view */}
      <NodesList $visible={!isCollapsed}>
        {sortedNodes.length === 0 ? (
          <EmptyState>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <p>No nodes assigned to this map</p>
          </EmptyState>
        ) : (
          sortedNodes.map((node, index) => {
            const isMain = node.nodeType?.toLowerCase() === 'main';
            const isExpanded = expandedNodeId === node.id;
            const positioned = isNodePositioned(node);

            return (
              <NodeCard
                key={node.id}
                $isSelected={selectedNodeId === node.id}
                $isPositioned={positioned}
              >
                <NodeCardHeader onClick={() => handleNodeClick(node)}>
                  <NodeIcon $type={node.nodeType} $isMain={isMain}>
                    {getNodeDisplayNumber(index, isMain)}
                  </NodeIcon>
                  <NodeInfo>
                    <NodeName>{node.friendlyName || node.nodeCode}</NodeName>
                    {node.position && (
                      <NodeLocationWrapper>
                        <NodeLocation
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltipPosition({
                              top: rect.top - 10,
                              left: rect.left,
                            });
                            setTooltipNodeId(node.id);
                          }}
                          onMouseLeave={() => {
                            setTooltipNodeId(null);
                          }}
                        >
                          {node.position}
                        </NodeLocation>
                        {tooltipNodeId === node.id && (
                          <NodeLocationTooltip
                            style={{
                              top: tooltipPosition.top,
                              left: tooltipPosition.left,
                              transform: 'translateY(-100%)',
                            }}
                          >
                            {node.position}
                          </NodeLocationTooltip>
                        )}
                      </NodeLocationWrapper>
                    )}
                    <NodeStatus $positioned={positioned}>
                      {positioned ? (
                        <>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                          </svg>
                          Placed
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                          </svg>
                          Not placed
                        </>
                      )}
                    </NodeStatus>
                  </NodeInfo>
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    style={{
                      width: 20,
                      height: 20,
                      color: '#666',
                      transform: isExpanded ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  >
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                  </svg>
                </NodeCardHeader>

                {isExpanded && (
                  <NodeCardControls>
                    <ActionButtons>
                      <ActionButton
                        onClick={() => onPlaceOnCanvas(node)}
                        disabled={node.locked || positioned}
                      >
                        Place on map
                      </ActionButton>
                      <ActionButton
                        $variant="danger"
                        onClick={() => onPlaceOnCanvas(node)}
                        disabled={node.locked || !positioned}
                      >
                        Reset to center
                      </ActionButton>
                    </ActionButtons>
                  </NodeCardControls>
                )}
              </NodeCard>
            );
          })
        )}
      </NodesList>

      {/* Collapsed view */}
      <CollapsedIcons $visible={isCollapsed}>
        {sortedNodes.map((node, index) => {
          const isMain = node.nodeType?.toLowerCase() === 'main';
          const positioned = isNodePositioned(node);

          return (
            <CollapsedNodeIcon
              key={node.id}
              $isPositioned={positioned}
              onClick={() => {
                onSelectNode(node.id);
                if (onToggleCollapse) onToggleCollapse();
              }}
              title={node.friendlyName || node.nodeCode}
            >
              {getNodeDisplayNumber(index, isMain)}
            </CollapsedNodeIcon>
          );
        })}
      </CollapsedIcons>

      <Instructions $visible={!isCollapsed && selectedNodeId !== null}>
        Click on the map to place the selected node or drag it
      </Instructions>
    </ManagerContainer>
  );
};

export default NodePositionManager;
